import { z } from "zod";

const color = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const px = (min: number, max: number) => z.number().int().min(min).max(max);

// Empty overrides deliberately preserve every rule of the current chat UI.
// Only bounded, typed properties are accepted; arbitrary CSS/HTML is never stored.
export const chatDesignSchema = z.object({
  accent: color.optional(), background: color.optional(),
  assistantBackground: color.optional(), assistantText: color.optional(),
  userBackground: color.optional(), userText: color.optional(),
  bubbleRadius: px(0, 32).optional(), bubbleWidth: px(40, 85).optional(),
  bubblePadding: px(8, 28).optional(), fontSize: px(14, 22).optional(),
  messageGap: px(8, 40).optional(), avatarSize: px(28, 64).optional(),
  buttonColor: color.optional(), buttonText: color.optional(),
  buttonSize: px(44, 72).optional(), buttonRadius: px(0, 36).optional(),
  buttonPosition: z.enum(["left", "right"]).optional(),
  buttonInset: px(4, 18).optional(),
  barColor: color.optional(), barTrack: color.optional(),
  barHeight: px(2, 16).optional(), barGap: px(2, 20).optional(),
  barSpacing: px(8, 40).optional(),
  inputBorder: color.optional(), inputBackground: color.optional(),
  inputFill: color.optional(), inputFillOpacity: px(0, 30).optional(),
  inputHeight: px(100, 200).optional(), inputRadius: px(0, 24).optional(),
  optionsBackground: color.optional(), optionsRadius: px(0, 24).optional(),
  contentWidth: px(760, 1280).optional(), sidePadding: px(8, 48).optional(),
  bottomPadding: px(0, 32).optional(),
  startBackground: color.optional(), startRadius: px(0, 32).optional(),
}).strict();

export type ChatDesign = z.infer<typeof chatDesignSchema>;
export type DesignKey = keyof ChatDesign;
export interface DesignRecord {
  draft: ChatDesign;
  published: ChatDesign;
  previous: ChatDesign | null;
  revision: number;
  updatedAt: string | null;
  publishedAt: string | null;
}
export const emptyDesignRecord = (): DesignRecord => ({
  draft: {}, published: {}, previous: null, revision: 0,
  updatedAt: null, publishedAt: null,
});
export const designMutationSchema = z.object({
  action: z.enum(["save", "publish", "restore"]),
  revision: z.number().int().nonnegative(),
  design: chatDesignSchema,
}).strict();
export type DesignMutation = z.infer<typeof designMutationSchema>;

export const previewScreens = ["start", "question1", "question2", "question3", "question4", "followup", "contact", "success"] as const;
export type PreviewScreen = typeof previewScreens[number];
export const previewMessageSchema = z.object({
  type: z.literal("atama-design-preview"), design: chatDesignSchema,
  screen: z.enum(previewScreens), demoMode: z.boolean(),
});

export function createDesignCss(input: ChatDesign): string {
  const d = chatDesignSchema.parse(input);
  const rules: string[] = [];
  const add = (selector: string, property: string, value: string | number | undefined, unit = "") => {
    if (value !== undefined) rules.push(`${selector}{${property}:${value}${unit} !important}`);
  };
  add(":root", "--blue", d.accent);
  add(":root", "--blue-dark", d.accent);
  add("html,body,.page-shell", "background-color", d.background);
  add(".message-row.assistant .message-bubble:not(.demo-followup-bubble)", "background-color", d.assistantBackground);
  add(".message-row.assistant .message-bubble:not(.demo-followup-bubble)", "color", d.assistantText);
  add(".message-row.user .message-bubble", "background-color", d.userBackground);
  add(".message-row.user .message-bubble", "border-color", d.userBackground);
  add(".message-row.user .message-bubble", "color", d.userText);
  add(".message-bubble", "border-radius", d.bubbleRadius, "px");
  add(".message-content,.message-row.user .message-content", "max-width", d.bubbleWidth, "%");
  add(".message-content", "width", d.bubbleWidth, "%");
  add(".message-bubble", "padding", d.bubblePadding, "px");
  add(".message-bubble,.composer textarea", "font-size", d.fontSize, "px");
  add(".message-list", "gap", d.messageGap, "px");
  add(".avatar", "width", d.avatarSize, "px");
  add(".avatar", "height", d.avatarSize, "px");
  add(".send-answer,.start-button,.submit-inquiry", "background-color", d.buttonColor);
  add(".send-answer,.start-button,.submit-inquiry", "color", d.buttonText);
  for (const prop of ["width", "height", "min-width", "min-height"]) add(".send-answer", prop, d.buttonSize, "px");
  add(".send-answer", "padding", d.buttonSize !== undefined ? 0 : undefined);
  add(".send-answer,.start-button,.submit-inquiry", "border-radius", d.buttonRadius, "px");
  if (d.buttonPosition !== undefined || d.buttonSize !== undefined || d.buttonInset !== undefined) {
    const position = d.buttonPosition ?? "right";
    const inset = d.buttonInset ?? 10;
    add(".send-answer", position, inset, "px");
    add(".send-answer", position === "left" ? "right" : "left", "auto");
    add(".send-answer", "bottom", inset, "px");
    add(".composer textarea", "padding-inline-start", position === "left" ? (d.buttonSize ?? 52) + inset + 12 : 18, "px");
    add(".composer textarea", "padding-inline-end", position === "right" ? (d.buttonSize ?? 52) + inset + 12 : 18, "px");
  }
  add(".question-steps>span.current,.question-steps>span.completed", "background-color", d.barColor ?? (d.barTrack ? "var(--blue)" : undefined));
  add(".question-steps>span", "background-color", d.barTrack);
  // The active selectors have greater specificity than the track selector.
  add(".question-steps>span", "height", d.barHeight, "px");
  add(".question-steps", "gap", d.barGap, "px");
  add(".progress-block", "padding-bottom", d.barSpacing, "px");
  add(".composer textarea,.field-grid input", "border-color", d.inputBorder);
  if (d.inputBackground !== undefined || d.inputFill !== undefined || d.inputFillOpacity !== undefined) {
    const hex = d.inputFill ?? "#0e5de8";
    const rgb = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(",");
    const base = d.inputBackground ?? "#ffffff";
    rules.push(`.composer textarea{background:linear-gradient(to top,rgba(${rgb},${(d.inputFillOpacity ?? 15) / 100}) 0 var(--answer-fill,0%),transparent var(--answer-fill,0%) 100%),${base} !important}`);
  }
  if (d.inputHeight !== undefined) {
    for (const prop of ["height", "min-height", "max-height"]) add(".composer textarea", prop, d.inputHeight, "px");
    rules.push(`@media(min-width:761px){.composer{height:clamp(${d.inputHeight + 225}px,50vh,${d.inputHeight + 245}px) !important}}`);
  }
  add(".composer textarea,.field-grid input", "border-radius", d.inputRadius, "px");
  add(".answer-groups.is-split", "background-color", d.optionsBackground);
  add(".answer-groups.is-split", "border-radius", d.optionsRadius, "px");
  if (d.contentWidth !== undefined) add(".chat-main", "width", `min(100%,${d.contentWidth}px)`);
  add(".chat-main", "padding-inline", d.sidePadding, "px");
  add(".chat-main", "padding-bottom", d.bottomPadding, "px");
  add(".start-card", "background-color", d.startBackground);
  add(".start-card", "border-radius", d.startRadius, "px");
  return rules.join("\n");
}
